import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import OfferDesktopFlightToggle from '../Filters/FlightToggle/OfferDesktopFlightToggle'
import useFlightPrice from 'hooks/useFlightPrice'
import moment from 'moment'
import useOfferPageFlightToggleDisplayPrice from 'components/OfferPage/FlightDeal/useOfferPageFlightToggleDisplayPrice'
import { useAppSelector } from 'hooks/reduxHooks'
import isAuDomesticAirportRoute from 'lib/flights/isAuDomesticAirportRoute'
import { FLIGHTS_MAX_LEAD_TIME_DAYS } from 'constants/flight'

interface Props {
  offer: App.HotelOffer | App.BundleOffer | App.VillaOffer
  rooms: Array<OfferPageRoom>
  checkInDate?: string
  checkOutDate?: string
  airport?: App.Airport
}

interface NoDateDisplayPriceProps {
  airportCode?: string
  bundledWithFlightsOnly?: boolean
  disabled?: boolean
  isAuDomesticFlight?: boolean
  totalFlightPrice?: number
}

export function HotelDesktopFlightToggle({
  offer,
  rooms,
  checkInDate,
  checkOutDate,
  airport,
}: Props) {
  const nights = checkInDate && checkOutDate ? moment(checkOutDate).diff(checkInDate, 'days') : 1
  const noDatesSelected = !checkInDate && !checkOutDate
  const departureTooFarInFuture = !!checkInDate && moment(checkInDate).diff(moment.utc(), 'days') > FLIGHTS_MAX_LEAD_TIME_DAYS
  const airports = useAppSelector(state => state.geo.airports)
  const isAuDomesticFlight = isAuDomesticAirportRoute({
    airports,
    departureAirportCode: airport?.code,
    destinationAirportCode: offer.flightDestinationPort,
  })

  const [flightPrice] = useFlightPrice(
    offer.offerFlightsEnabled && !departureTooFarInFuture ? offer.flightDestinationPort : undefined,
    rooms,
    checkInDate,
    checkOutDate,
    airport?.code,
    nights,
    { exactSabrePrice: !noDatesSelected },
  )

  if (departureTooFarInFuture) return null

  if (noDatesSelected) {
    return <HotelDesktopFlightToggleWithNoDateDisplayPrice
      airportCode={airport?.code}
      bundledWithFlightsOnly={offer.bundledWithFlightsOnly}
      disabled={!offer.offerFlightsEnabled || offer.bundledWithFlightsOnly}
      isAuDomesticFlight={isAuDomesticFlight}
      totalFlightPrice={flightPrice}
    />
  }

  return <OfferDesktopFlightToggle
    bundledWithFlightsOnly={offer.bundledWithFlightsOnly}
    isAuDomesticFlight={isAuDomesticFlight}
    totalFlightPrice={flightPrice}
  />
}

function HotelDesktopFlightToggleWithNoDateDisplayPrice({
  airportCode,
  bundledWithFlightsOnly,
  disabled,
  isAuDomesticFlight,
  totalFlightPrice,
}: NoDateDisplayPriceProps) {
  const displayFlightPrice = useOfferPageFlightToggleDisplayPrice({
    airportCode,
    disabled,
  })

  return <OfferDesktopFlightToggle
    bundledWithFlightsOnly={bundledWithFlightsOnly}
    displayFlightPrice={displayFlightPrice}
    isAuDomesticFlight={isAuDomesticFlight}
    totalFlightPrice={totalFlightPrice}
  />
}

type MappedProps = Pick<Props,
  | 'offer'
  | 'rooms'
  | 'checkInDate'
  | 'checkOutDate'
  | 'airport'
>

export default withOfferPageState((offerState): MappedProps => ({
  airport: offerState.airport,
  offer: offerState.offer!,
  rooms: offerState.rooms,
  checkInDate: offerState.checkInDate,
  checkOutDate: offerState.checkOutDate,
}), HotelDesktopFlightToggle)
